import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import {
  format,
  isValid,
  parseISO,
  subSeconds,
  subMinutes,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';

interface QuickRange {
  label: string;
  from: string;
  to: string;
}

// Stable list of quick ranges, exported in case the parent wants to pre-select.
export const QUICK_RANGES: QuickRange[] = [
  { label: 'Last 1 day', from: 'now-1d', to: 'now' },
  { label: 'Last 3 days', from: 'now-3d', to: 'now' },
  { label: 'Last 5 days', from: 'now-5d', to: 'now' },
  { label: 'Last 7 days', from: 'now-7d', to: 'now' },
  { label: 'Last 10 days', from: 'now-10d', to: 'now' },
  { label: 'Last 15 days', from: 'now-15d', to: 'now' },
  { label: 'Last 21 days', from: 'now-21d', to: 'now' },
  { label: 'Last 1 month', from: 'now-1M', to: 'now' },
  { label: 'Last 3 months', from: 'now-3M', to: 'now' },
  { label: 'Last 6 months', from: 'now-6M', to: 'now' },
  { label: 'Last 1 year', from: 'now-1y', to: 'now' },
];

export function evaluateRangeExpr(expr: string | null | undefined): Date | null {
  if (!expr) return null;
  const trimmed = expr.trim();
  if (!trimmed) return null;
  if (trimmed === 'now') return new Date();

  const m = trimmed.match(/^now-(\d+)([smhdwMy])$/);
  if (m) {
    const n = parseInt(m[1], 10);
    const unit = m[2];
    const now = new Date();
    switch (unit) {
      case 's':
        return subSeconds(now, n);
      case 'm':
        return subMinutes(now, n);
      case 'h':
        return subHours(now, n);
      case 'd':
        return subDays(now, n);
      case 'w':
        return subWeeks(now, n);
      case 'M':
        return subMonths(now, n);
      case 'y':
        return subYears(now, n);
    }
  }

  const parsed = parseISO(trimmed);
  if (isValid(parsed)) return parsed;
  return null;
}

interface DateTimeRangePickerProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({ from, to, onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempFrom, setTempFrom] = useState(from ?? '');
  const [tempTo, setTempTo] = useState(to ?? '');
  const [search, setSearch] = useState('');
  const [calendarTarget, setCalendarTarget] = useState<'from' | 'to' | null>(null);
  const fromBtnRef = useRef<HTMLButtonElement>(null);
  const toBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setTempFrom(from ?? '');
    setTempTo(to ?? '');
  }, [from, to]);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    setCalendarTarget(null);
    setSearch('');
    setTempFrom(from ?? '');
    setTempTo(to ?? '');
  };

  const handleApply = () => {
    onChange(tempFrom.trim() || null, tempTo.trim() || null);
    setAnchorEl(null);
    setCalendarTarget(null);
  };

  const handleQuickRange = (range: QuickRange) => {
    onChange(range.from, range.to);
    setAnchorEl(null);
    setCalendarTarget(null);
  };

  const tempFromDate = useMemo(() => evaluateRangeExpr(tempFrom), [tempFrom]);
  const tempToDate = useMemo(() => evaluateRangeExpr(tempTo), [tempTo]);
  const fromError = !!tempFrom.trim() && !tempFromDate;
  const toError = !!tempTo.trim() && !tempToDate;
  const orderError =
    !!tempFromDate && !!tempToDate && tempFromDate.getTime() > tempToDate.getTime();
  const canApply = !!tempFromDate && !!tempToDate && !fromError && !toError && !orderError;

  const displayValue = useMemo(() => {
    if (!from || !to) return 'Select Time Range';
    const matchingQuick = QUICK_RANGES.find((r) => r.from === from && r.to === to);
    if (matchingQuick) return matchingQuick.label;
    const f = evaluateRangeExpr(from);
    const t = evaluateRangeExpr(to);
    if (!f || !t) return 'Invalid range';
    return `${format(f, 'yyyy-MM-dd')} → ${format(t, 'yyyy-MM-dd')}`;
  }, [from, to]);

  const filteredRanges = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return QUICK_RANGES;
    return QUICK_RANGES.filter((r) => r.label.toLowerCase().includes(q));
  }, [search]);

  const browserTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const handleCalendarPick = (value: Date | null) => {
    if (!value || !isValid(value)) return;
    const dateStr = format(value, 'yyyy-MM-dd');
    if (calendarTarget === 'from') setTempFrom(dateStr);
    else if (calendarTarget === 'to') setTempTo(dateStr);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Trigger */}
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px',
          padding: '8.5px 12px',
          cursor: 'pointer',
          backgroundColor: theme.palette.background.paper,
          minWidth: '180px',
          '&:hover': { borderColor: theme.palette.text.primary },
        }}
      >
        <CalendarTodayIcon
          sx={{ mr: 1, fontSize: '20px', color: theme.palette.text.secondary }}
        />
        <Typography variant="body2" noWrap>
          {displayValue}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: theme.shadows[10],
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 480, alignItems: 'flex-start' }}>
          {/* LEFT: Absolute time range */}
          <Box sx={{ flex: 1, p: 2, minWidth: 260 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Absolute time range
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              From
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={tempFrom}
              onChange={(e) => setTempFrom(e.target.value)}
              placeholder="now-1h"
              error={fromError}
              helperText={
                fromError
                  ? 'Invalid expression'
                  : tempFromDate
                    ? format(tempFromDate, 'yyyy-MM-dd')
                    : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      ref={fromBtnRef}
                      size="small"
                      aria-label="open calendar for from"
                      onClick={() =>
                        setCalendarTarget(calendarTarget === 'from' ? null : 'from')
                      }
                    >
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              To
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={tempTo}
              onChange={(e) => setTempTo(e.target.value)}
              placeholder="now"
              error={toError}
              helperText={
                toError
                  ? 'Invalid expression'
                  : tempToDate
                    ? format(tempToDate, 'yyyy-MM-dd')
                    : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      ref={toBtnRef}
                      size="small"
                      aria-label="open calendar for to"
                      onClick={() =>
                        setCalendarTarget(calendarTarget === 'to' ? null : 'to')
                      }
                    >
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            {orderError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mb: 1 }}
              >
                "From" must be before "To".
              </Typography>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleApply}
              disabled={!canApply}
              sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
              Apply time range
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5 }}
            >
              Browser time {browserTz}. Sent to server as UTC.
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* RIGHT: Quick ranges */}
          <Box sx={{ width: 200, p: 2, display: 'flex', flexDirection: 'column' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search quick ranges"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <List
              dense
              disablePadding
              sx={{ maxHeight: 280, overflowY: 'auto' }}
            >
              {filteredRanges.map((range) => {
                const isSelected = from === range.from && to === range.to;
                return (
                  <ListItemButton
                    key={range.label}
                    onClick={() => handleQuickRange(range)}
                    selected={isSelected}
                    sx={{
                      borderRadius: 1,
                      borderLeft: `3px solid ${
                        isSelected ? theme.palette.primary.main : 'transparent'
                      }`,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <ListItemText primary={range.label} />
                  </ListItemButton>
                );
              })}
              {filteredRanges.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                  No matching ranges
                </Typography>
              )}
            </List>
          </Box>
        </Box>
      </Popover>

      {/* Calendar sub-popover, anchored to whichever icon button was clicked.
          Rendered separately so the main popover stays compact regardless of
          whether the calendar is open. */}
      <Popover
        open={calendarTarget !== null}
        anchorEl={
          calendarTarget === 'from'
            ? fromBtnRef.current
            : calendarTarget === 'to'
              ? toBtnRef.current
              : null
        }
        onClose={() => setCalendarTarget(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '8px', boxShadow: theme.shadows[10] } }}
      >
        <StaticDatePicker
          value={
            calendarTarget === 'from' ? tempFromDate ?? null : tempToDate ?? null
          }
          onAccept={(value) => {
            handleCalendarPick(value);
            setCalendarTarget(null);
          }}
          onChange={() => {
          }}
          slotProps={{
            actionBar: { actions: ['cancel', 'accept'] },
            toolbar: { hidden: true },
          }}
        />
      </Popover>
    </LocalizationProvider>
  );
};

export default DateTimeRangePicker;
